import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Award, Users, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import HotelPartners from '../components/HotelPartners';
import { destinations } from '../data/destinations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Thank you for subscribing!');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Four hero destinations hand-picked from the master list for the landing page.
  // Using verified 4K images so there are zero broken assets.
  const featuredDestinations = [
    { ...destinations.find((d) => d.name === 'Udaipur'), description: 'Royal Palace Experiences' },
    { ...destinations.find((d) => d.name === 'Maldives'), description: 'Private Island Retreats' },
    { ...destinations.find((d) => d.name === 'Swiss Alps'), description: 'Alpine Luxury Chalets' },
    { ...destinations.find((d) => d.name === 'Serengeti'), description: 'Exclusive Safari Adventures' },
  ];

  const experiences = [
    {
      title: 'Private Yacht Charters',
      description: 'Sail the world\'s most exclusive waters in ultimate luxury',
      icon: Globe,
    },
    {
      title: 'Luxury Safari Journeys',
      description: 'Witness nature\'s grandeur from intimate luxury camps',
      icon: Award,
    },
    {
      title: 'Palace Stays',
      description: 'Experience royal heritage in India\'s finest palaces',
      icon: Sparkles,
    },
    {
      title: 'Bespoke Cultural Immersion',
      description: 'Deep cultural experiences crafted for discerning travelers',
      icon: Users,
    },
  ];

  const testimonials = [
    {
      quote: 'Sarvatra transformed our honeymoon into an unforgettable journey. Every detail was perfection.',
      author: 'Alexandra M.',
      location: 'New York',
    },
    {
      quote: 'The level of personalization and attention to detail exceeded all expectations. Truly world-class.',
      author: 'James R.',
      location: 'London',
    },
    {
      quote: 'Our family safari was flawlessly executed. Sarvatra understands luxury travel like no other.',
      author: 'Priya S.',
      location: 'Mumbai',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="homepage"
    >
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1564507592333-c60657eea523?q=80&w=3840&auto=format&fit=crop&ixlib=rb-4.0.3"
            alt="Taj Mahal at Sunrise"
            className="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 container-padding text-center max-w-5xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-8"
            data-testid="hero-subtitle"
          >
            Sarvatra Journeys
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-heading text-5xl md:text-7xl lg:text-9xl font-light tracking-tight leading-[0.9] text-primary mb-8"
            data-testid="hero-title"
          >
            Journeys Without
            <br />
            Boundaries
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="font-body text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed"
            data-testid="hero-description"
          >
            Bespoke luxury travel experiences crafted for the discerning explorer.
            Where every journey is a masterpiece.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
          >
            <Link to="/concierge" className="btn-primary" data-testid="hero-cta-button">
              Plan Your Journey
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="section-padding container-padding" data-testid="featured-destinations-section">
        <div className="max-w-[1920px] mx-auto">
          <div className="text-center mb-16 md:mb-24">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">
              Discover
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-normal tracking-tight text-primary/90">
              Featured Destinations
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredDestinations.map((destination, index) => (
              <motion.div
                key={destination.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="destination-card group relative overflow-hidden aspect-[3/4] cursor-pointer"
                data-testid={`destination-card-${destination.name.toLowerCase()}`}
              >
                <img
                  src={destination.image}
                  alt={destination.name}
                  className="destination-card-image absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <h3 className="font-heading text-3xl md:text-4xl font-light text-primary mb-2">
                    {destination.name}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">{destination.description}</p>
                  <Link
                    to="/destinations"
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 btn-secondary inline-flex items-center gap-2"
                    data-testid={`explore-${destination.name.toLowerCase()}-btn`}
                  >
                    Explore <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Experiences */}
      <section className="section-padding container-padding bg-white/[0.02]" data-testid="signature-experiences-section">
        <div className="max-w-[1920px] mx-auto">
          <div className="text-center mb-16 md:mb-24">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">
              Curated for You
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-normal tracking-tight text-primary/90">
              Signature Experiences
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {experiences.map((exp, index) => {
              const Icon = exp.icon;
              return (
                <motion.div
                  key={exp.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="glass-card p-8"
                  data-testid={`experience-card-${index}`}
                >
                  <Icon className="text-accent mb-6" size={40} />
                  <h3 className="font-heading text-2xl font-light text-primary mb-4">
                    {exp.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {exp.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hotel Partners */}
      <HotelPartners variant="full" />

      {/* Why Travel With Sarvatra */}
      <section className="section-padding container-padding" data-testid="why-sarvatra-section">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">
              Why Choose Us
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-normal tracking-tight text-primary/90 mb-8">
              The Art of the Extraordinary
            </h2>
            <p className="font-body text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              At Sarvatra, we believe travel is an art form. Every journey we craft is a bespoke
              masterpiece, meticulously designed to reflect your desires, exceed your expectations,
              and create memories that transcend time. Our global network of luxury partnerships
              and deep cultural insights ensure access to the world's most exclusive experiences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding container-padding bg-white/[0.02]" data-testid="testimonials-section">
        <div className="max-w-[1920px] mx-auto">
          <div className="text-center mb-16 md:mb-24">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">
              Client Stories
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-normal tracking-tight text-primary/90">
              What They Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="glass-card p-10"
                data-testid={`testimonial-card-${index}`}
              >
                <p className="font-accent text-lg italic text-primary/90 mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-body text-sm font-medium text-primary">
                    {testimonial.author}
                  </p>
                  <p className="font-body text-xs text-muted-foreground">
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding container-padding" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-6">
              Begin Your Journey
            </h2>
            <p className="text-muted-foreground text-sm md:text-base mb-10">
              Subscribe to receive exclusive travel insights, destination guides, and curated experiences.
            </p>

            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="underline-input flex-1 px-4 py-4 text-primary font-body text-sm"
                data-testid="newsletter-email-input"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary whitespace-nowrap"
                data-testid="newsletter-submit-btn"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Instagram Gallery */}
      <section className="section-padding" data-testid="instagram-gallery-section">
        <div className="max-w-[1920px] mx-auto">
          <div className="text-center mb-12 container-padding">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">
              @sarvatrajourneys
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-primary">
              Follow Our Journeys
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
            <div className="aspect-square overflow-hidden group cursor-pointer" data-testid="instagram-image-1">
              <img
                src="https://images.unsplash.com/photo-1620483829646-8fb1d8162108?q=80&w=600&auto=format&fit=crop"
                alt="Instagram 1"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="aspect-square overflow-hidden group cursor-pointer" data-testid="instagram-image-2">
              <img
                src="https://images.unsplash.com/photo-1770665567877-72ee8a7c9051?q=80&w=600&auto=format&fit=crop"
                alt="Instagram 2"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="aspect-square overflow-hidden group cursor-pointer" data-testid="instagram-image-3">
              <img
                src="https://images.unsplash.com/photo-1709402606682-400133d92ab2?q=80&w=600&auto=format&fit=crop"
                alt="Instagram 3"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="aspect-square overflow-hidden group cursor-pointer" data-testid="instagram-image-4">
              <img
                src="https://images.unsplash.com/photo-1526392060635-9d6019884377?q=80&w=600&auto=format&fit=crop"
                alt="Instagram 4"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="aspect-square overflow-hidden group cursor-pointer" data-testid="instagram-image-5">
              <img
                src="https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=600&auto=format&fit=crop"
                alt="Instagram 5"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className="aspect-square overflow-hidden group cursor-pointer" data-testid="instagram-image-6">
              <img
                src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=600&auto=format&fit=crop"
                alt="Instagram 6"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding container-padding text-center" data-testid="final-cta-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <h2 className="font-heading text-5xl md:text-7xl font-light text-primary mb-8">
            Your Extraordinary Journey Awaits
          </h2>
          <p className="text-muted-foreground text-base md:text-lg mb-12 max-w-2xl mx-auto">
            Let us craft a bespoke travel experience that transcends the ordinary.
          </p>
          <Link to="/concierge" className="btn-primary" data-testid="final-cta-button">
            Start Planning
          </Link>
        </motion.div>
      </section>
    </motion.div>
  );
};

export default HomePage;